import React, { useEffect, useRef, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { courseService, campusService } from '../../api';
import toast from 'react-hot-toast';
import {
    ArrowLeft,
    BookOpen,
    Copy,
    ImagePlus,
    Info,
    Save,
    Trash2,
    Upload,
    Users,
} from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AppSelect from '../../components/ui/AppSelect'
import RandIcon from '../../components/icons/RandIcon'
import '../../styles/course-form.css'

const COURSE_IMAGE_MAX_SIZE = 8 * 1024 * 1024
const COURSE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']

function AdminCourseForm() {
    const { id } = useParams();
    const location = useLocation();
    const isEdit = Boolean(id);
    const duplicateFromId = location.state?.duplicateFrom;
    const navigate = useNavigate();

    const [campuses, setCampuses] = useState([]);
    const [loading, setLoading] = useState(isEdit || Boolean(duplicateFromId));
    const [saving, setSaving] = useState(false);
    const [imageFile, setImageFile] = useState(null)
    const [imagePreview, setImagePreview] = useState('')
    const [imageError, setImageError] = useState('')
    const [removeImage, setRemoveImage] = useState(false)
    const imageInputRef = useRef(null)
    const objectUrlRef = useRef('')

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        level: 'beginner',
        status: 'draft',
        campus: '',
        max_students: 50,
        course_price: 0.00,
    });

    useEffect(() => {
        fetchCampuses();
        if (isEdit) {
            fetchCourse(id, true);
        } else if (duplicateFromId) {
            fetchCourse(duplicateFromId, false);
        }
    }, [id, duplicateFromId]);

    useEffect(() => () => {
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }, [])

    const fetchCampuses = async () => {
        try {
            const res = await campusService.getCampuses();
            setCampuses(res.data.results || res.data);
            // Set default campus if not editing/duplicating and campuses exist
            if (!isEdit && !duplicateFromId && (res.data.results?.length > 0 || res.data.length > 0)) {
                const firstId = res.data.results ? res.data.results[0].id : res.data[0].id;
                setFormData(prev => ({ ...prev, campus: firstId }));
            }
        } catch (err) {
            toast.error('Failed to load campuses');
        }
    };

    const fetchCourse = async (courseId, forEdit = true) => {
        try {
            const res = await courseService.getCourseDetail(courseId);
            const data = res.data;
            setFormData({
                title: forEdit ? data.title : `${data.title} (Copy)`,
                description: data.description,
                level: data.level,
                status: forEdit ? data.status : 'draft', // duplicated courses start as draft
                campus: data.campus,
                max_students: data.max_students,
                course_price: data.course_price,
            });
            setImagePreview(forEdit ? (data.thumbnail || data.image || '') : '')
            setImageFile(null)
            setRemoveImage(false)
            setImageError('')
            if (!forEdit) {
                toast.success('Course details loaded for duplication');
            }
        } catch (err) {
            toast.error('Failed to load course details');
            navigate('/admin/courses');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageSelection = (file) => {
        if (!file) return

        if (!COURSE_IMAGE_TYPES.includes(file.type)) {
            setImageError('Choose a JPEG, PNG, or WebP image.')
            return
        }
        if (file.size > COURSE_IMAGE_MAX_SIZE) {
            setImageError('Choose an image smaller than 8 MB.')
            return
        }

        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
        const objectUrl = URL.createObjectURL(file)
        objectUrlRef.current = objectUrl
        setImageFile(file)
        setImagePreview(objectUrl)
        setRemoveImage(false)
        setImageError('')
    }

    const handleImageChange = (event) => {
        handleImageSelection(event.target.files?.[0])
        event.target.value = ''
    }

    const handleImageDrop = (event) => {
        event.preventDefault()
        handleImageSelection(event.dataTransfer.files?.[0])
    }

    const handleRemoveImage = () => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current)
            objectUrlRef.current = ''
        }
        setImageFile(null)
        setImagePreview('')
        setImageError('')
        setRemoveImage(isEdit)
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.campus) {
            toast.error('Select a campus before saving the course.')
            return
        }
        setSaving(true);
        try {
            const payload = new FormData()
            Object.entries(formData).forEach(([key, value]) => {
                payload.append(key, String(value))
            })
            if (imageFile) payload.append('image', imageFile)
            if (isEdit && removeImage) payload.append('remove_image', 'true')

            if (isEdit) {
                await courseService.updateCourse(id, payload);
                toast.success('Course updated successfully');
            } else {
                await courseService.createCourse(payload);
                toast.success('Course created successfully');
            }
            navigate('/admin/courses');
        } catch (err) {
            const responseData = err.response?.data
            const imageMessage = Array.isArray(responseData?.image)
                ? responseData.image[0]
                : responseData?.image
            toast.error(imageMessage || responseData?.message || 'Failed to save course');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-2xl text-center">Loading course data...</div>;

    const pageTitle = isEdit ? 'Edit Course' : (duplicateFromId ? 'Duplicate Course' : 'Create New Course');

    return (
        <DashboardLayout>
            <main className="page-container content-width course-editor-page">
                <button
                    type="button"
                    onClick={() => navigate('/admin/courses')}
                    className="course-editor-back"
                >
                    <ArrowLeft size={16} />
                    Back to courses
                </button>

                <div className="course-editor-shell">
                    <header className="course-editor-header">
                        <div className="course-editor-heading">
                            <div className="course-editor-heading-icon">
                                {duplicateFromId && !isEdit ? <Copy size={22} /> : <BookOpen size={22} />}
                            </div>
                            <div>
                                <p className="course-editor-eyebrow">Course workspace</p>
                                <h1>{pageTitle}</h1>
                                <p>Shape how this course appears and how learners join it.</p>
                            </div>
                        </div>
                        <div className={`course-editor-status${formData.status === 'published' ? ' is-published' : ''}`}>
                            {formData.status}
                        </div>
                    </header>

                    <form onSubmit={handleSubmit} className="course-editor-form">
                        <div className="course-editor-identity">
                            <section className="course-editor-section">
                                <div className="course-editor-section-heading">
                                    <span>01</span>
                                    <div>
                                        <h2>Course identity</h2>
                                        <p>Give learners a clear title, context, and home campus.</p>
                                    </div>
                                </div>

                                <div className="course-editor-field-grid">
                                    <label className="course-editor-field">
                                        <span>Course title</span>
                                        <input
                                            type="text"
                                            name="title"
                                            required
                                            placeholder="e.g. Full Stack Web Development"
                                            value={formData.title}
                                            onChange={handleChange}
                                        />
                                    </label>

                                    <div className="course-editor-field">
                                        <label htmlFor="course-campus">Campus</label>
                                        <AppSelect
                                            id="course-campus"
                                            name="campus"
                                            required
                                            className="course-editor-select"
                                            value={formData.campus}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select a campus</option>
                                            {campuses.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </AppSelect>
                                    </div>
                                </div>

                                <label className="course-editor-field">
                                    <span>Description</span>
                                    <textarea
                                        name="description"
                                        required
                                        rows={6}
                                        placeholder="Describe what learners will achieve, who this is for, and what makes the course useful."
                                        value={formData.description}
                                        onChange={handleChange}
                                    />
                                </label>
                            </section>

                            <aside className="course-cover-panel">
                                <div className="course-cover-heading">
                                    <div>
                                        <p className="course-editor-eyebrow">Course card artwork</p>
                                        <h2>Cover image</h2>
                                    </div>
                                    <ImagePlus size={20} aria-hidden="true" />
                                </div>

                                <input
                                    ref={imageInputRef}
                                    id="course-image"
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    className="sr-only"
                                    onChange={handleImageChange}
                                />

                                <button
                                    type="button"
                                    className={`course-cover-dropzone${imagePreview ? ' has-image' : ''}${imageError ? ' has-error' : ''}`}
                                    onClick={() => imageInputRef.current?.click()}
                                    onDragOver={(event) => event.preventDefault()}
                                    onDrop={handleImageDrop}
                                    aria-describedby="course-image-help course-image-error"
                                >
                                    {imagePreview ? (
                                        <>
                                            <img
                                                src={imagePreview}
                                                alt={`${formData.title || 'Course'} cover preview`}
                                            />
                                            <span className="course-cover-change">
                                                <Upload size={16} aria-hidden="true" />
                                                Replace image
                                            </span>
                                        </>
                                    ) : (
                                        <span className="course-cover-empty">
                                            <span className="course-cover-empty-icon">
                                                <ImagePlus size={26} aria-hidden="true" />
                                            </span>
                                            <strong>Add a course cover</strong>
                                            <small>Drop an image here or choose a file</small>
                                        </span>
                                    )}
                                </button>

                                <div className="course-cover-actions">
                                    <button
                                        type="button"
                                        className="course-cover-action"
                                        onClick={() => imageInputRef.current?.click()}
                                    >
                                        <Upload size={15} aria-hidden="true" />
                                        {imagePreview ? 'Choose another' : 'Choose image'}
                                    </button>
                                    {imagePreview && (
                                        <button
                                            type="button"
                                            className="course-cover-action danger"
                                            onClick={handleRemoveImage}
                                        >
                                            <Trash2 size={15} aria-hidden="true" />
                                            Remove
                                        </button>
                                    )}
                                </div>
                                <p id="course-image-help" className="course-cover-help">
                                    JPEG, PNG, or WebP · up to 8 MB · 16:9 recommended
                                </p>
                                <p id="course-image-error" className="course-cover-error" aria-live="polite">
                                    {imageError}
                                </p>
                            </aside>
                        </div>

                        <section className="course-editor-section course-editor-settings">
                            <div className="course-editor-section-heading">
                                <span>02</span>
                                <div>
                                    <h2>Publishing and access</h2>
                                    <p>Set the learner level, visibility, pricing, and enrolment rules.</p>
                                </div>
                            </div>

                            <div className="course-settings-grid">
                                <div className="course-editor-field">
                                    <label htmlFor="course-level">
                                        <Info size={14} /> Level
                                    </label>
                                    <AppSelect
                                        id="course-level"
                                        name="level"
                                        className="course-editor-select"
                                        value={formData.level}
                                        onChange={handleChange}
                                    >
                                        <option value="beginner">Beginner</option>
                                        <option value="intermediate">Intermediate</option>
                                        <option value="advanced">Advanced</option>
                                    </AppSelect>
                                </div>

                                <div className="course-editor-field">
                                    <label htmlFor="course-status">
                                        <Info size={14} /> Status
                                    </label>
                                    <AppSelect
                                        id="course-status"
                                        name="status"
                                        className="course-editor-select"
                                        value={formData.status}
                                        onChange={handleChange}
                                    >
                                        <option value="draft">Draft</option>
                                        <option value="published">Published</option>
                                        <option value="archived">Archived</option>
                                    </AppSelect>
                                </div>

                                <label className="course-editor-field">
                                    <span>
                                        <RandIcon size={14} /> Price (ZAR)
                                    </span>
                                    <input
                                        type="number"
                                        name="course_price"
                                        min="0"
                                        step="0.01"
                                        value={formData.course_price}
                                        onChange={handleChange}
                                    />
                                </label>

                                <label className="course-editor-field">
                                    <span>
                                        <Users size={14} /> Maximum learners
                                    </span>
                                    <input
                                        type="number"
                                        name="max_students"
                                        min="1"
                                        value={formData.max_students}
                                        onChange={handleChange}
                                    />
                                </label>
                            </div>

                        </section>

                        <footer className="course-editor-footer">
                            <p>
                                {isEdit
                                    ? 'Changes will update the course wherever learners see it.'
                                    : 'You can add subjects and lessons after creating the course.'}
                            </p>
                            <div className="course-editor-footer-actions">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/courses')}
                                className="btn btn-outline"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="btn btn-primary"
                            >
                                {saving ? (
                                    <>
                                        <span className="course-editor-spinner" aria-hidden="true" />
                                        Saving…
                                    </>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        {isEdit ? 'Update Course' : (duplicateFromId ? 'Save Duplicated Course' : 'Create Course')}
                                    </>
                                )}
                            </button>
                            </div>
                        </footer>
                    </form>
                </div>
            </main>
        </DashboardLayout>
    );
}

export default AdminCourseForm;
